import React, {useMemo} from 'react';

import {useSavedListTypes, useSavedSearchTypes} from 'src/hooks/dictionaries/useAlertDictionaries';
import {NoResultPage} from 'src/modules/entitlements';
import {transformItemToTableSearch} from 'src/modules/profile/user/tabs/content/utils/transform-item-to-table-search';
import {AdvancedSearchType} from 'src/openapi/admin/models/AdvancedSearchType';
import {DateRangeFilter} from 'src/openapi/admin/models/DateRangeFilter';
import {SavedListType} from 'src/openapi/admin/models/SavedListType';
import {SavedSearchRequestSortField} from 'src/openapi/admin/models/SavedSearchRequest';
import {SortDirection} from 'src/openapi/admin/models/SortDirection';

import {PaginationChangeHandler, PaginationStripe} from '@pb/kit-component-pagination';
import Table, {Body, Cell, Row} from '@pb/kit-component-table';
import {pluralize} from '@pb/kit-formatters';

import {SIZES} from '../constants';
import {DataSection, SavedSearchType, TablesTypes} from '../typedef';

import {RequestItem} from './request-item';
import {TableHeaders} from './table-headers';

const templates: Record<SavedSearchType, string> = {
    SEARCH: '0 search[es]',
    LIST: '0 list[s]',
};

const CAPTION = {
    OWNER: 'saved',
    SHARED: 'shared',
};

type Props = {
    data: DataSection,
    savedSearchType: SavedSearchType,
    savedTableType: TablesTypes,
    isEmptySearchCriteria?: boolean,
    currentPage: number,
    pageSize: number,
    onPaginationChange: PaginationChangeHandler,
    totalCount: number,
    onPageSizeChange?: (currentPage: number, newPageSize: number) => void,
    userId: number,
    onSortChange?: (field: SavedSearchRequestSortField, direction: SortDirection) => void,
    onTypeFilterChange?: (filter: AdvancedSearchType[]) => void,
    onListTypeFilterChange?: (filter: SavedListType[]) => void,
    onDateFilterChange?: (field: 'createDate' | 'lastRun' | 'lastShared', filter: DateRangeFilter | undefined) => void,
    isLoading: boolean,
    alertsLoading: boolean,
};

export const SavedTable = (props: Props) => {
    const {
        data,
        savedSearchType,
        savedTableType,
        isEmptySearchCriteria = true,
        currentPage,
        pageSize,
        onPaginationChange,
        totalCount,
        onPageSizeChange,
        onSortChange,
        onTypeFilterChange,
        onListTypeFilterChange,
        onDateFilterChange,
        userId,
        isLoading,
        alertsLoading,
    } = props;
    const {savedSearchTypes} = useSavedSearchTypes();
    const {savedListTypes} = useSavedListTypes();
    const items = useMemo(() => Object.values(data), [data]);

    const getTypeDescription = (code: string | undefined) =>
        (savedSearchType === 'SEARCH' ? savedSearchTypes : savedListTypes)?.find(item => item.code === code)?.description || code || '';


    const pluralized = pluralize(templates[savedSearchType], 0).replace(/^0\s*/, '');


    return (
        <>
            <Table
                fixed={true}
                align="middle">
                <TableHeaders
                    savedSearchType={savedSearchType}
                    onSortChange={onSortChange}
                    onTypeFilterChange={onTypeFilterChange}
                    onListTypeFilterChange={onListTypeFilterChange}
                    onDateFilterChange={onDateFilterChange}
                />

                {(items && items.length > 0) ? (
                    <Body>
                        {items.map((item, i) => (
                            <RequestItem
                                key={item.savedSearchInfo.id}
                                showZebra={i % 2 === 0}
                                userId={userId}
                                search={transformItemToTableSearch(item, savedSearchType, getTypeDescription)}
                                isLoading={isLoading}
                                alertsLoading={alertsLoading}
                            />
                        ))}
                    </Body>
                ) : (
                    <Body>
                        <Row>
                            <Cell colSpan={9} className="text-align_center">
                                <NoResultPage
                                    title="No data to show"
                                    margin={200}
                                    description={isEmptySearchCriteria
                                        ? `This user has not created any ${CAPTION[savedTableType]} ${pluralized} yet.`
                                        : 'No data found for the selected criteria.'}
                                />
                            </Cell>
                        </Row>
                    </Body>
                )}
            </Table>
            <PaginationStripe
                totalCount={totalCount}
                currentPage={currentPage}
                pageSize={pageSize}
                goToPage={true}
                onPaginationChange={onPaginationChange}
                showSizeSwitcher={true}
                sizeSwitcherPosition="RIGHT"
                sizes={SIZES}
                onChangePaginationSize={onPageSizeChange}
            />
        </>
    );
};
